import * as React from 'react';
import axios from 'axios';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Grid,
  Link,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import useAuthorize from '../hooks/useAuthorize';
import authProvider from '../auth/authProvider';
import { SEO_KEYWORD_WRITE_ROLES, SEO_PORTAL_ROLES } from '../roles/portalRoles';
import { getApiBaseUrl } from '../utils/apiBaseUrl';
import {
  PAGE_TYPE_LABELS,
  buildPageOptions,
  filterPageOptions,
  listCategories,
  normalizeUrl,
  type PageOption,
  type PageType,
  type TrafficMode,
} from './productSeoPageUtils';

type MonitoredKeywordRow = {
  id: number;
  keyword_text: string;
  page_url: string | null;
  search_volume: number | null;
  is_active: boolean;
};

type RankingRow = {
  data_date: string;
  keyword: string;
  page_url: string;
  country: string;
  device: string;
  clicks: number;
  impressions: number;
  ctr_percentage: number;
  average_position: number | null;
  approx_serp_page: number | null;
  ranking_zone: string;
};

type MovementRow = {
  keyword: string;
  page_url: string;
  previous_average_position: number | null;
  current_average_position: number | null;
  position_improvement: number | null;
  impressions: number;
  clicks: number;
  ctr_percentage: number;
  current_date: string;
  previous_date: string;
};

type ListResponse<T> = {
  count: number;
  data: T[];
};

const getApiBase = (): string => {
  const root = String(getApiBaseUrl() || '').trim();
  if (!root) return '/api';
  if (root.endsWith('/api')) return root;
  if (root.endsWith('/')) return `${root}api`;
  return `${root}/api`;
};

const getAuthHeaders = (): Record<string, string> => {
  const token = localStorage.getItem('user_authToken');
  if (!token) return {};
  return { Authorization: `Bearer ${token}` };
};

const formatNumber = (value: number | null | undefined, digits = 2): string => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '--';
  return Number(value).toFixed(digits);
};

const formatInteger = (value: number | null | undefined): string => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '--';
  return Number(value).toLocaleString();
};

const formatSigned = (value: number | null | undefined): string => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '--';
  const shown = Number(value);
  const sign = shown > 0 ? '+' : '';
  return `${sign}${shown.toFixed(2)}`;
};

const addDays = (date: Date, days: number): Date => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};

const toIso = (date: Date): string => date.toISOString().slice(0, 10);

const DashboardProductSeo: React.FC = () => {
  useAuthorize(SEO_PORTAL_ROLES);
  const apiBase = React.useMemo(() => getApiBase(), []);

  const defaultEndDate = React.useMemo(() => toIso(addDays(new Date(), -3)), []);
  const defaultStartDate = React.useMemo(() => toIso(addDays(new Date(), -33)), []);
  const defaultPreviousDate = React.useMemo(() => toIso(addDays(new Date(), -10)), []);

  const [startDate, setStartDate] = React.useState<string>(defaultStartDate);
  const [endDate, setEndDate] = React.useState<string>(defaultEndDate);
  const [currentDate, setCurrentDate] = React.useState<string>(defaultEndDate);
  const [previousDate, setPreviousDate] = React.useState<string>(defaultPreviousDate);
  const [country, setCountry] = React.useState<string>('in');
  const [device, setDevice] = React.useState<string>('');
  const [keywordFilter, setKeywordFilter] = React.useState<string>('');
  const [pageTypeFilter, setPageTypeFilter] = React.useState<PageType | 'all'>('all');
  const [categoryFilter, setCategoryFilter] = React.useState<string>('all');
  const [trafficMode, setTrafficMode] = React.useState<TrafficMode>('strict_page');

  const [loadingProducts, setLoadingProducts] = React.useState<boolean>(true);
  const [loadingData, setLoadingData] = React.useState<boolean>(true);
  const [error, setError] = React.useState<string>('');
  const [rankings, setRankings] = React.useState<RankingRow[]>([]);
  const [movement, setMovement] = React.useState<MovementRow[]>([]);
  const [monitoredKeywords, setMonitoredKeywords] = React.useState<MonitoredKeywordRow[]>([]);
  const [pageOptions, setPageOptions] = React.useState<PageOption[]>([]);
  const [selectedPageUrl, setSelectedPageUrl] = React.useState<string>('');
  const [seeding, setSeeding] = React.useState<boolean>(false);
  const [seedMessage, setSeedMessage] = React.useState<string>('');

  const canSeedKeywords = React.useMemo(
    () => authProvider.isRoleAuthorized(SEO_KEYWORD_WRITE_ROLES),
    [],
  );

  const loadPageOptions = React.useCallback(async () => {
    setLoadingProducts(true);
    setError('');
    try {
      const response = await axios.get<ListResponse<MonitoredKeywordRow>>(
        `${apiBase}/seo/monitored-keywords`,
        { headers: getAuthHeaders() }
      );

      const rows = (response.data.data || []).filter(
        (row) => row.is_active && row.page_url,
      );
      setMonitoredKeywords(rows);
      setPageOptions(buildPageOptions(rows));
    } catch (err: any) {
      const message = err?.response?.data?.error || err?.message || 'Failed to load SEO page list';
      setError(message);
    } finally {
      setLoadingProducts(false);
    }
  }, [apiBase]);

  const filteredPageOptions = React.useMemo(
    () => filterPageOptions(pageOptions, pageTypeFilter, categoryFilter),
    [pageOptions, pageTypeFilter, categoryFilter],
  );

  const categoryOptions = React.useMemo(
    () => listCategories(pageOptions),
    [pageOptions],
  );

  const selectedPage = React.useMemo(
    () => pageOptions.find((option) => option.pageUrl === selectedPageUrl) || null,
    [pageOptions, selectedPageUrl],
  );

  React.useEffect(() => {
    setSelectedPageUrl((current) => {
      if (current && filteredPageOptions.some((item) => item.pageUrl === current)) return current;
      return filteredPageOptions[0]?.pageUrl || '';
    });
  }, [filteredPageOptions]);

  const monitoredKeywordSet = React.useMemo(() => {
    if (!selectedPageUrl) return new Set<string>();
    return new Set(
      monitoredKeywords
        .filter((row) => row.page_url && normalizeUrl(String(row.page_url)) === selectedPageUrl)
        .map((row) => row.keyword_text.toLowerCase()),
    );
  }, [monitoredKeywords, selectedPageUrl]);

  const loadData = React.useCallback(async () => {
    if (!selectedPageUrl) {
      setRankings([]);
      setMovement([]);
      setLoadingData(false);
      return;
    }

    setLoadingData(true);
    setError('');
    try {
      const strictPage = trafficMode === 'strict_page';
      const [rankingsRes, movementRes] = await Promise.all([
        axios.get<ListResponse<RankingRow>>(`${apiBase}/seo/rankings`, {
          params: {
            startDate,
            endDate,
            pageUrl: strictPage ? selectedPageUrl : undefined,
            keyword: keywordFilter || undefined,
            country,
            device: device || undefined,
            limit: 1000,
          },
          headers: getAuthHeaders(),
        }),
        axios.get<ListResponse<MovementRow>>(`${apiBase}/seo/keyword-movement`, {
          params: {
            currentDate,
            previousDate,
            pageUrl: strictPage ? selectedPageUrl : undefined,
            country,
            device: device || undefined,
            limit: 500,
          },
          headers: getAuthHeaders(),
        }),
      ]);

      let rankingRows = rankingsRes.data.data || [];
      let movementRows = movementRes.data.data || [];

      if (!strictPage) {
        rankingRows = rankingRows.filter((row) => monitoredKeywordSet.has(row.keyword.toLowerCase()));
        movementRows = movementRows.filter((row) => monitoredKeywordSet.has(row.keyword.toLowerCase()));
      }

      setRankings(rankingRows);
      setMovement(movementRows);
    } catch (err: any) {
      const message = err?.response?.data?.error || err?.message || 'Failed to load SEO page data';
      setError(message);
    } finally {
      setLoadingData(false);
    }
  }, [
    apiBase,
    startDate,
    endDate,
    currentDate,
    previousDate,
    selectedPageUrl,
    keywordFilter,
    country,
    device,
    trafficMode,
    monitoredKeywordSet,
  ]);

  React.useEffect(() => {
    void loadPageOptions();
  }, [loadPageOptions]);

  React.useEffect(() => {
    void loadData();
  }, [loadData]);

  const runStrategySeed = React.useCallback(async () => {
    setSeeding(true);
    setSeedMessage('');
    try {
      const response = await axios.post<{
        pages_processed?: number;
        keywords_upserted?: number;
        product_keywords_deactivated?: number;
        page_types?: Record<string, number>;
      }>(
        `${apiBase}/seo/seed-monitored-keywords`,
        {
          include_subcategories: true,
          deactivate_product_pages: true,
          pull_gsc: false,
        },
        { headers: getAuthHeaders() },
      );
      const types = response.data.page_types || {};
      const typeSummary = Object.entries(types)
        .map(([key, count]) => `${key}: ${count}`)
        .join(', ');
      setSeedMessage(
        `Seeded ${response.data.keywords_upserted || 0} keywords across ${response.data.pages_processed || 0} pages`
        + (typeSummary ? ` (${typeSummary})` : '')
        + `. Deactivated ${response.data.product_keywords_deactivated || 0} product-page keyword rows.`,
      );
      await loadPageOptions();
      await loadData();
    } catch (err: any) {
      const message = err?.response?.data?.error || err?.message || 'Failed to seed monitored keywords';
      setSeedMessage(message);
    } finally {
      setSeeding(false);
    }
  }, [apiBase, loadData, loadPageOptions]);

  const pageTypeCounts = React.useMemo(() => {
    const counts = new Map<PageType, number>();
    for (const option of pageOptions) {
      counts.set(option.pageType, (counts.get(option.pageType) || 0) + 1);
    }
    return counts;
  }, [pageOptions]);

  const keywordsForSelectedPage = React.useMemo(() => {
    const list = monitoredKeywords
      .filter((row) => row.page_url && normalizeUrl(String(row.page_url)) === selectedPageUrl)
      .map((row) => ({ text: row.keyword_text, searchVolume: row.search_volume }));

    const map = new Map<string, typeof list[0]>();
    for (const item of list) {
      if (!map.has(item.text.toLowerCase())) map.set(item.text.toLowerCase(), item);
    }
    return Array.from(map.values()).sort((a, b) => a.text.localeCompare(b.text));
  }, [monitoredKeywords, selectedPageUrl]);

  const currentDateInData = React.useMemo(() => {
    if (rankings.length === 0) return null;
    return rankings
      .map((row) => row.data_date)
      .sort((a, b) => b.localeCompare(a))[0] || null;
  }, [rankings]);

  const currentRows = React.useMemo(() => {
    if (!currentDateInData) return [];
    return rankings.filter((row) => row.data_date === currentDateInData);
  }, [rankings, currentDateInData]);

  const latestByKeyword = React.useMemo(() => {
    const sorted = [...rankings].sort((a, b) => {
      if (a.keyword === b.keyword) return b.data_date.localeCompare(a.data_date);
      return a.keyword.localeCompare(b.keyword);
    });
    const map = new Map<string, RankingRow>();
    for (const row of sorted) {
      const key = row.keyword.toLowerCase();
      if (!map.has(key)) {
        map.set(key, row);
      }
    }
    return map;
  }, [rankings]);

  const summary = React.useMemo(() => {
    const impressions = currentRows.reduce((sum, row) => sum + (Number(row.impressions) || 0), 0);
    const clicks = currentRows.reduce((sum, row) => sum + (Number(row.clicks) || 0), 0);
    const weightedPosition = currentRows.reduce((sum, row) => {
      const position = Number(row.average_position);
      const weight = Math.max(1, Number(row.impressions) || 0);
      return Number.isFinite(position) ? sum + (position * weight) : sum;
    }, 0);
    const totalWeight = currentRows.reduce((sum, row) => sum + Math.max(1, Number(row.impressions) || 0), 0);
    const avgPosition = totalWeight > 0 ? weightedPosition / totalWeight : null;
    const ctr = impressions > 0 ? (clicks / impressions) * 100 : 0;
    const totalSearchVolume = keywordsForSelectedPage.reduce((sum, k) => sum + (Number(k.searchVolume) || 0), 0);

    return {
      monitoredKeywords: keywordsForSelectedPage.length,
      keywordsWithData: new Set(currentRows.map((row) => row.keyword.toLowerCase())).size,
      totalSearchVolume,
      impressions,
      clicks,
      ctr,
      avgPosition,
      improvingKeywords: movement.filter((row) => (row.position_improvement || 0) > 0).length,
    };
  }, [currentRows, keywordsForSelectedPage, movement]);

  const trendRows = React.useMemo(() => {
    const buckets = new Map<string, { impressions: number; clicks: number; weightedPos: number; weight: number }>();
    for (const row of rankings) {
      const date = row.data_date;
      if (!buckets.has(date)) {
        buckets.set(date, { impressions: 0, clicks: 0, weightedPos: 0, weight: 0 });
      }
      const bucket = buckets.get(date)!;
      const impressions = Number(row.impressions) || 0;
      const clicks = Number(row.clicks) || 0;
      const avgPos = Number(row.average_position);
      bucket.impressions += impressions;
      bucket.clicks += clicks;
      if (Number.isFinite(avgPos)) {
        const weight = Math.max(1, impressions);
        bucket.weightedPos += avgPos * weight;
        bucket.weight += weight;
      }
    }

    return Array.from(buckets.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([date, value]) => {
        const ctr = value.impressions > 0 ? (value.clicks / value.impressions) * 100 : 0;
        const avgPosition = value.weight > 0 ? value.weightedPos / value.weight : null;
        return {
          date,
          impressions: value.impressions,
          clicks: value.clicks,
          ctr,
          avgPosition,
        };
      });
  }, [rankings]);

  const showRankingPageColumn = trafficMode === 'site_wide';

  return (
    <Box sx={{ p: 2 }}>
      <Card>
        <CardHeader
          title="Page SEO"
          subheader="Track product pages, category tabs, legacy landings, and hub URLs with monitored keywords, movement, and GSC traffic."
        />
        <CardContent>
          <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
            {Array.from(pageTypeCounts.entries()).map(([pageType, count]) => (
              <Chip
                key={pageType}
                size="small"
                label={`${PAGE_TYPE_LABELS[pageType]}: ${count}`}
                variant={pageTypeFilter === pageType ? 'filled' : 'outlined'}
                onClick={() => setPageTypeFilter((current) => (current === pageType ? 'all' : pageType))}
              />
            ))}
            <Chip size="small" label={`Total pages: ${pageOptions.length}`} />
            {canSeedKeywords ? (
              <Button
                size="small"
                variant="outlined"
                disabled={seeding}
                onClick={() => void runStrategySeed()}
              >
                {seeding ? 'Seeding…' : 'Seed strategy URLs'}
              </Button>
            ) : null}
          </Stack>

          {pageOptions.length === 0 && !loadingProducts ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              No monitored URLs yet. Category, subcategory, hub, and landing pages are populated from the SEO
              strategy when you run <strong>Seed strategy URLs</strong> (admin) or
              {' '}
              <code>npm run seo:seed:category-keywords</code>
              {' '}
              on the marketing API.
            </Alert>
          ) : null}

          {seedMessage ? (
            <Alert
              severity={seedMessage.toLowerCase().includes('failed') ? 'error' : 'success'}
              sx={{ mb: 2 }}
              onClose={() => setSeedMessage('')}
            >
              {seedMessage}
            </Alert>
          ) : null}

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mb: 2, flexWrap: 'wrap' }}>
            <Select
              size="small"
              value={pageTypeFilter}
              onChange={(event) => setPageTypeFilter(event.target.value as PageType | 'all')}
              sx={{ minWidth: 160 }}
            >
              <MenuItem value="all">All page types</MenuItem>
              {(Object.keys(PAGE_TYPE_LABELS) as PageType[]).map((pageType) => (
                <MenuItem key={pageType} value={pageType}>{PAGE_TYPE_LABELS[pageType]}</MenuItem>
              ))}
            </Select>
            <Select
              size="small"
              value={categoryFilter}
              onChange={(event) => setCategoryFilter(String(event.target.value))}
              sx={{ minWidth: 130 }}
            >
              <MenuItem value="all">All categories</MenuItem>
              {categoryOptions.map((category) => (
                <MenuItem key={category} value={category}>{category}</MenuItem>
              ))}
            </Select>
            <Select
              size="small"
              value={selectedPageUrl}
              onChange={(event) => setSelectedPageUrl(String(event.target.value))}
              sx={{ minWidth: 360 }}
              displayEmpty
            >
              {filteredPageOptions.length === 0 ? (
                <MenuItem value="">No tracked URLs found — run category/product seed scripts</MenuItem>
              ) : null}
              {filteredPageOptions.map((option) => (
                <MenuItem key={option.pageUrl} value={option.pageUrl}>
                  [{PAGE_TYPE_LABELS[option.pageType]}] {option.label}
                  {option.category ? ` · ${option.category}` : ''} ({option.keywordCount} kw)
                </MenuItem>
              ))}
            </Select>
            <Select
              size="small"
              value={trafficMode}
              onChange={(event) => setTrafficMode(event.target.value as TrafficMode)}
              sx={{ minWidth: 170 }}
            >
              <MenuItem value="strict_page">Strict page traffic</MenuItem>
              <MenuItem value="site_wide">Site-wide keyword traffic</MenuItem>
            </Select>
          </Stack>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
            <TextField
              size="small"
              type="date"
              label="Start Date"
              value={startDate}
              onChange={(event) => setStartDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              size="small"
              type="date"
              label="End Date"
              value={endDate}
              onChange={(event) => setEndDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              size="small"
              label="Keyword filter"
              value={keywordFilter}
              onChange={(event) => setKeywordFilter(event.target.value)}
              placeholder="Optional keyword search"
              sx={{ minWidth: 200 }}
            />
            <Select
              size="small"
              value={country}
              onChange={(event) => setCountry(String(event.target.value))}
              sx={{ minWidth: 100 }}
            >
              <MenuItem value="in">India</MenuItem>
            </Select>
            <Select
              size="small"
              value={device}
              onChange={(event) => setDevice(String(event.target.value))}
              sx={{ minWidth: 130 }}
              displayEmpty
            >
              <MenuItem value="">All Devices</MenuItem>
              <MenuItem value="desktop">Desktop</MenuItem>
              <MenuItem value="mobile">Mobile</MenuItem>
              <MenuItem value="tablet">Tablet</MenuItem>
            </Select>
            <Button variant="contained" onClick={() => void loadData()}>
              Apply
            </Button>
          </Stack>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
            <TextField
              size="small"
              type="date"
              label="Movement Current Date"
              value={currentDate}
              onChange={(event) => setCurrentDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              size="small"
              type="date"
              label="Movement Previous Date"
              value={previousDate}
              onChange={(event) => setPreviousDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <Button
              variant="outlined"
              onClick={() => {
                setKeywordFilter('');
                setStartDate(defaultStartDate);
                setEndDate(defaultEndDate);
                setCurrentDate(defaultEndDate);
                setPreviousDate(defaultPreviousDate);
                setCountry('in');
                setDevice('');
                setPageTypeFilter('all');
                setCategoryFilter('all');
                setTrafficMode('strict_page');
              }}
            >
              Reset
            </Button>
          </Stack>

          {selectedPageUrl ? (
            <Typography variant="body2" sx={{ mb: 2 }}>
              {selectedPage ? PAGE_TYPE_LABELS[selectedPage.pageType] : 'Page'}:{' '}
              <Link href={selectedPageUrl} target="_blank" rel="noreferrer">
                {selectedPageUrl}
              </Link>
              {trafficMode === 'site_wide' ? (
                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  (showing keyword traffic across all landing URLs)
                </Typography>
              ) : null}
            </Typography>
          ) : null}

          {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
          {loadingProducts || loadingData ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              Loading SEO page data...
            </Alert>
          ) : null}

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={2}>
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Typography variant="body2" color="text.secondary">Monitored Keywords</Typography>
                <Typography variant="h6">{formatInteger(summary.monitoredKeywords)}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Typography variant="body2" color="text.secondary">Total Search Vol</Typography>
                <Typography variant="h6">{formatInteger(summary.totalSearchVolume)}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Typography variant="body2" color="text.secondary">Keywords With Data</Typography>
                <Typography variant="h6">{formatInteger(summary.keywordsWithData)}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Typography variant="body2" color="text.secondary">Impressions</Typography>
                <Typography variant="h6">{formatInteger(summary.impressions)}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Typography variant="body2" color="text.secondary">Clicks</Typography>
                <Typography variant="h6">{formatInteger(summary.clicks)}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Typography variant="body2" color="text.secondary">Avg Position</Typography>
                <Typography variant="h6">{formatNumber(summary.avgPosition)}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Typography variant="body2" color="text.secondary">CTR / Improving</Typography>
                <Typography variant="h6">{formatNumber(summary.ctr)}%</Typography>
                <Typography variant="caption" color="text.secondary">
                  +{formatInteger(summary.improvingKeywords)} improving
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Keywords For This Page
          </Typography>
          <Table size="small" sx={{ mb: 3 }}>
            <TableHead>
              <TableRow>
                <TableCell>Keyword</TableCell>
                {showRankingPageColumn ? <TableCell>Ranking Page</TableCell> : null}
                <TableCell align="right">Search Vol</TableCell>
                <TableCell align="right">Avg Position</TableCell>
                <TableCell>Ranking Zone</TableCell>
                <TableCell align="right">Impressions</TableCell>
                <TableCell align="right">Clicks</TableCell>
                <TableCell align="right">CTR</TableCell>
                <TableCell>Latest Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {keywordsForSelectedPage.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={showRankingPageColumn ? 9 : 8}>
                    <Typography variant="body2" color="text.secondary">
                      No monitored keywords mapped to this URL. Run seed scripts on the marketing API.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : null}
              {keywordsForSelectedPage.map((keywordItem) => {
                const keyword = keywordItem.text;
                const latest = latestByKeyword.get(keyword.toLowerCase());
                return (
                  <TableRow key={keyword}>
                    <TableCell>{keyword}</TableCell>
                    {showRankingPageColumn ? (
                      <TableCell sx={{ maxWidth: 280, wordBreak: 'break-all' }}>
                        {latest?.page_url || '--'}
                      </TableCell>
                    ) : null}
                    <TableCell align="right">{formatInteger(keywordItem.searchVolume)}</TableCell>
                    <TableCell align="right">{formatNumber(latest?.average_position ?? null)}</TableCell>
                    <TableCell>{latest?.ranking_zone || '--'}</TableCell>
                    <TableCell align="right">{formatInteger(latest?.impressions ?? null)}</TableCell>
                    <TableCell align="right">{formatInteger(latest?.clicks ?? null)}</TableCell>
                    <TableCell align="right">{formatNumber(latest?.ctr_percentage ?? null)}%</TableCell>
                    <TableCell>{latest?.data_date || '--'}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>

          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Week-over-Week Keyword Movement
          </Typography>
          <Table size="small" sx={{ mb: 3 }}>
            <TableHead>
              <TableRow>
                <TableCell>Keyword</TableCell>
                {showRankingPageColumn ? <TableCell>Page</TableCell> : null}
                <TableCell align="right">Previous Position</TableCell>
                <TableCell align="right">Current Position</TableCell>
                <TableCell align="right">Movement</TableCell>
                <TableCell align="right">Impressions</TableCell>
                <TableCell align="right">Clicks</TableCell>
                <TableCell align="right">CTR</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {movement.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={showRankingPageColumn ? 8 : 7}>
                    <Typography variant="body2" color="text.secondary">
                      No movement rows found for the selected dates.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : null}
              {movement.map((row) => (
                <TableRow key={`${row.keyword}-${row.page_url}`}>
                  <TableCell>{row.keyword}</TableCell>
                  {showRankingPageColumn ? (
                    <TableCell sx={{ maxWidth: 280, wordBreak: 'break-all' }}>{row.page_url}</TableCell>
                  ) : null}
                  <TableCell align="right">{formatNumber(row.previous_average_position)}</TableCell>
                  <TableCell align="right">{formatNumber(row.current_average_position)}</TableCell>
                  <TableCell align="right">{formatSigned(row.position_improvement)}</TableCell>
                  <TableCell align="right">{formatInteger(row.impressions)}</TableCell>
                  <TableCell align="right">{formatInteger(row.clicks)}</TableCell>
                  <TableCell align="right">{formatNumber(row.ctr_percentage)}%</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Traffic Trend (By Week Snapshot)
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Snapshot Date</TableCell>
                <TableCell align="right">Impressions</TableCell>
                <TableCell align="right">Clicks</TableCell>
                <TableCell align="right">CTR</TableCell>
                <TableCell align="right">Avg Position</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {trendRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Typography variant="body2" color="text.secondary">
                      No trend data available for this page and date range.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : null}
              {trendRows.map((row) => (
                <TableRow key={row.date}>
                  <TableCell>{row.date}</TableCell>
                  <TableCell align="right">{formatInteger(row.impressions)}</TableCell>
                  <TableCell align="right">{formatInteger(row.clicks)}</TableCell>
                  <TableCell align="right">{formatNumber(row.ctr)}%</TableCell>
                  <TableCell align="right">{formatNumber(row.avgPosition)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </Box>
  );
};

export default DashboardProductSeo;
